import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Flex, Spinner, Text,
} from '@chakra-ui/react';
import { LuChevronRight } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { fetchGroupRows } from '../../api/endpointsBackend';
import { similarLine } from '../../utils/similarMovements';
import PhoneSheet, { usePhoneSheet } from './PhoneSheet';
import { usePhoneShell } from './PhoneShellContext';
import { ListCard, MovementRows, SectionCaption } from './PhoneReadOnlyParts';

// Without a rhythm or a summary, the card says how big the two groups are.
const groupsLine = (similar, t) => [
  similar.same_charge_total > 0 && t('phone.sheet.similar.sameCharge', { count: similar.same_charge_total }),
  similar.other_total > 0 && t('phone.sheet.similar.otherVisits', { count: similar.other_total }),
].filter(Boolean).join(' · ');

/**
 * The list behind the card: the same charge, then the other visits, as the
 * phone rows everywhere else. A row opens that movement in place of this one.
 */
function SimilarList({ similar }) {
  const { t } = useTranslation('movements');
  const { close } = usePhoneSheet();
  const { openMovement } = usePhoneShell();
  const leaving = useRef(false);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchGroupRows({
      filters: [{ entity: 'Movement', column: 'id', operator: 'in', values: similar.ids }],
      orderBy: 'date_desc',
      limit: similar.ids.length,
    })
      .then((data) => { if (!cancelled) setRows(data.items || []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [similar]);

  // The sheet goes first: a movement opened under it would be undone.
  const open = async (id) => {
    if (leaving.current) return;
    leaving.current = true;
    await close();
    openMovement(id);
  };

  const line = similarLine(similar, t);
  const groups = [
    { key: 'sameCharge', ids: similar.same_charge_ids, total: similar.same_charge_total },
    { key: 'otherVisits', ids: similar.other_ids, total: similar.other_total },
  ].filter((g) => g.ids.length > 0);

  return (
    <Box>
      {line && <Text textStyle="phoneMeta" color="phone.muted" px="16px">{line}</Text>}
      {rows === null ? (
        <MovementRows movements={[]} loading onOpen={open} />
      ) : groups.map((g, i) => {
        const inGroup = new Set(g.ids);
        return (
          <Box key={g.key}>
            <SectionCaption pt={i === 0 ? '16px' : '24px'}>
              {t(`detailPanel.similar.${g.key}`, { count: g.total })}
            </SectionCaption>
            <MovementRows
              movements={rows.filter((m) => inGroup.has(m.id))}
              emptyLabel={t('common:states.empty')}
              onOpen={open}
            />
            {g.total > g.ids.length && (
              <Text textStyle="phoneMeta" color="phone.muted" px="16px" py="8px">
                {t('detailPanel.similar.more', { count: new Intl.NumberFormat('en-US').format(g.total - g.ids.length) })}
              </Text>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * The movement sheet's "Similar movements" card: the rhythm or the summary
 * (else the size of each group) and how many there are. A tap opens the
 * list. While the lookup runs the card says so, so nothing moves when it
 * lands; a movement with nothing similar shows no card. With `interactive`
 * off (edits not yet saved) the card is text: a row would open another
 * movement and drop them.
 */
export default function PhoneSimilarMovements({ similar, loading, interactive }) {
  const { t } = useTranslation(['movements', 'common']);
  const [listOpen, setListOpen] = useState(false);

  if (!loading && !similar?.ids?.length) return null;

  const tappable = !loading && interactive;
  return (
    <>
      <ListCard>
        <Flex
          as={tappable ? 'button' : 'div'}
          type={tappable ? 'button' : undefined}
          onClick={tappable ? () => setListOpen(true) : undefined}
          align="center"
          gap="12px"
          w="100%"
          minH="72px"
          px="14px"
          py="12px"
          textAlign="left"
          aria-busy={loading || undefined}
          _active={tappable ? { bg: 'redesign.bg.hover' } : undefined}
        >
          <Box flex="1" minW={0}>
            <Text fontSize="16px" lineHeight="22px" fontWeight="500" color="redesign.heading">
              {t('phone.sheet.similar.title')}
            </Text>
            <Text textStyle="phoneMeta" color="phone.muted" mt="2px">
              {loading ? t('common:states.loading') : (similarLine(similar, t) || groupsLine(similar, t))}
            </Text>
          </Box>
          {loading ? (
            // label overrides Chakra's hardcoded English "Loading...".
            <Spinner size="sm" color="phone.muted" label={t('detailPanel.similar.loading')} />
          ) : (
            <Text fontSize="16px" lineHeight="22px" fontWeight="600" color="redesign.text" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {new Intl.NumberFormat('en-US').format(similar.same_charge_total + similar.other_total)}
            </Text>
          )}
          {tappable && <Box as={LuChevronRight} boxSize="20px" color="phone.muted" flexShrink={0} />}
        </Flex>
      </ListCard>

      {similar?.ids?.length > 0 && (
        <PhoneSheet
          id="similar-movements"
          isOpen={listOpen}
          onClose={() => setListOpen(false)}
          size="detent"
          title={t('phone.sheet.similar.title')}
        >
          <SimilarList similar={similar} />
        </PhoneSheet>
      )}
    </>
  );
}
